import { buttonVariants } from "@loot-os/ui/components/button";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";

import { ErrorBanner } from "../components/ErrorBanner.js";
import { ForgeBuilder } from "../components/forge/ForgeBuilder.js";
import { ForgeUsernameGate } from "../components/forge/ForgeUsernameGate.js";
import { RequireAuth } from "../components/RequireAuth.js";
import { rowsFromDetail } from "../lib/forge.js";
import { boxQueryOptions, myForgeQueryOptions, qk } from "../lib/queries.js";
import { queryClient } from "../lib/queryClient.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";

/** PUT /v1/forge/boxes/{boxId}/drop-table: the builder, loaded with the published version. */
export function ForgeEditPage() {
  return (
    <RequireAuth>
      <ForgeUsernameGate>
        <EditForgeBox />
      </ForgeUsernameGate>
    </RequireAuth>
  );
}

function EditForgeBox() {
  const { boxId } = useParams({ from: "/forge/$boxId/edit" });
  const { box } = useTerminology();
  const { sessionToken } = useShopper();
  const mine = useQuery({ ...myForgeQueryOptions(), enabled: !!sessionToken });
  const detail = useQuery(boxQueryOptions(boxId));
  const owned = mine.data?.boxes.find((b) => b.id === boxId);

  const heading = (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Edit {box.singular}</h1>
      <p className="text-sm text-muted-foreground">
        Change the pull rates or your cut. The name and image stay as they are.
      </p>
    </div>
  );

  if (mine.isError || detail.isError) {
    const err = mine.error ?? detail.error;
    return (
      <div className="grid gap-6">
        {heading}
        <ErrorBanner>
          {err instanceof Error ? err.message : `failed to load the ${box.singular}`}
        </ErrorBanner>
      </div>
    );
  }
  if (mine.isPending || detail.isPending) {
    return (
      <div className="grid gap-6">
        {heading}
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }
  if (owned?.status !== "active") {
    return (
      <div className="grid justify-items-start gap-4">
        {heading}
        <p className="text-sm text-muted-foreground">
          {owned
            ? `Only a live ${box.singular} can be edited. Unarchive it first.`
            : `This ${box.singular} is not one of yours.`}
        </p>
        <Link to="/forge/mine" className={buttonVariants({ variant: "secondary" })}>
          Back to my {box.plural}
        </Link>
      </div>
    );
  }

  const version = detail.data;
  return (
    <div className="grid gap-6">
      {heading}
      <ForgeBuilder
        // A newer version remounts the builder with its rates.
        key={version.version}
        edit={{
          boxId,
          name: owned.name,
          imageUrl: owned.imageUrl,
          version: version.version,
          creatorCutPercent: owned.creatorCutPercent,
          rows: rowsFromDetail(version),
        }}
        onVersionChanged={() => {
          void queryClient.invalidateQueries({ queryKey: qk.box(boxId) });
          void queryClient.invalidateQueries({ queryKey: qk.myForge });
        }}
      />
    </div>
  );
}
